import { useState, useEffect } from 'react';
import ClientRow from '../../components/journey/ClientRow/ClientRow';
import ClientHeader from '../../components/journey/ClientHeader/ClientHeader';
import ClientProfileTab from '../ClientProfileTab/ClientProfileTab';
import { getClientList, getClientDetail } from '../../api/clientApi';
import { getActiveVersion } from '../../api/profileApi';
import './ClientProfile.css';

const MIN_WIDTH = 180;

export default function ClientProfile() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [profileVersion, setProfileVersion] = useState(null);
  const [versionLoading, setVersionLoading] = useState(true);

  const [searchText, setSearchText] = useState('');
  const [users, setUsers] = useState([]);

  const [p1Width, setP1Width] = useState(280);
  const [p1Collapsed, setP1Collapsed] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchActiveVersion();
    fetchUsers();
  }, []);

  async function fetchUsers() {
    try {
      const res = await fetch('/api/users');
      const data = await res.json();
      if (data.success) setUsers(data.data || []);
    } catch (err) {}
  }

  async function fetchClients() {
    try {
      setLoading(true);
      const data = await getClientList();
      setClients(data || []);
    } catch (err) {
      setError('Could not load clients.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchActiveVersion() {
    try {
      setVersionLoading(true);
      const version = await getActiveVersion();
      setProfileVersion(version);
    } catch (err) {
      setProfileVersion(null);
    } finally {
      setVersionLoading(false);
    }
  }

  async function handleSelectClient(client) {
    setSelectedClientId(client._id);
    try {
      setDetailLoading(true);
      const data = await getClientDetail(client._id);
      setSelectedClient(data);
    } catch (err) {
      setError('Could not load client detail.');
    } finally {
      setDetailLoading(false);
    }
  }

  function getFilteredClients() {
    if (!searchText.trim()) return clients;
    const lower = searchText.toLowerCase();
    const filtered = [];
    for (let i = 0; i < clients.length; i++) {
      const c = clients[i];
      if (
        (c.name || '').toLowerCase().includes(lower) ||
        (c.email || '').toLowerCase().includes(lower) ||
        (c.phone || '').toLowerCase().includes(lower)
      ) {
        filtered.push(c);
      }
    }
    return filtered;
  }

  function startDrag(e) {
    e.preventDefault();
    const startX = e.clientX;
    const startW = p1Width;

    function move(ev) {
      setP1Width(Math.max(MIN_WIDTH, Math.min(480, startW + (ev.clientX - startX))));
    }

    function end() {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', end);
    }

    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', end);
  }

  const filteredClients = getFilteredClients();

  if (loading || versionLoading) return <div className="loading-state">Loading...</div>;
  if (error) return <div className="error-state">{error}</div>;

  return (
    <div className="cp">

      {/* ── PANEL 1: Client list ── */}
      {!p1Collapsed && (
        <div className="cp__p1" style={{ width: p1Width }}>
          <div className="cp__p1-header">
            <h1 className="cp__page-title">Client Profiles</h1>
            <input
              className="cp__search"
              type="text"
              placeholder="Search name, email, phone..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
          </div>
          <div className="cp__p1-body">
            {filteredClients.length === 0 && (
              <p className="cp__list-empty">No clients match your search.</p>
            )}
            {filteredClients.map(client => (
              <ClientRow
                key={client._id}
                client={client}
                isSelected={selectedClientId === client._id}
                onClick={() => handleSelectClient(client)}
                users={users}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── DIVIDER ── */}
      <div className="cp__divider">
        {!p1Collapsed && (
          <div className="cp__divider-drag" onMouseDown={startDrag} />
        )}
        <button
          className="cp__divider-btn"
          onClick={() => setP1Collapsed(!p1Collapsed)}
          title={p1Collapsed ? 'Show clients' : 'Hide clients'}
        >
          {p1Collapsed ? '▶' : '◀'}
        </button>
      </div>

      {/* ── RIGHT AREA ── */}
      <div className="cp__right">
        {!selectedClient && !detailLoading && (
          <div className="cp__empty-state">
            <div className="cp__empty-icon">📋</div>
            <p className="cp__empty-title">Select a client</p>
            <p className="cp__empty-sub">Click any client on the left to view their profile.</p>
          </div>
        )}

        {detailLoading && <div className="loading-state">Loading client...</div>}

        {selectedClient && !detailLoading && (
          <div className="cp__right-inner">
            <ClientHeader client={selectedClient} users={users} />
            <div className="cp__content">
              <ClientProfileTab
                client={selectedClient}
                profileVersion={profileVersion}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
